import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, styles as dsStyles } from '@frontend/design-system';
import { getShortTimezoneText, getTimezoneOffset } from '../../../utils/date-helpers';
import { HOURS_LANE_WIDTH } from '../constants';
import { ADJUSTED_ONE_HOUR_IN_MINUTES, zIndex } from '../utils';

interface Props {
  hourLabels: string[];
  timezone: string;
  timezoneTextTopOffset?: number;
}

export const HoursOfTheDay = ({ hourLabels, timezone, timezoneTextTopOffset = 0 }: Props) => {
  const numberOfHours = hourLabels.length;

  return (
    <>
      <TimezoneText timezone={timezone} timezoneTextTopOffset={timezoneTextTopOffset} />
      <motion.section layout='position' css={styles.container(numberOfHours)}>
        {hourLabels.map((hour, index) => (
          <motion.div layout='position' key={hour + index} css={styles.item}>
            <Text css={{ margin: 0, userSelect: 'none', marginTop: theme.spacing(-1) }} size='small' color='light'>
              {hour}
            </Text>
          </motion.div>
        ))}
      </motion.section>
    </>
  );
};

const TimezoneText = ({ timezone, timezoneTextTopOffset }: Omit<Props, 'hourLabels'>) => {
  if (!timezone) return null;

  const shortTimezone = getShortTimezoneText(timezone);
  const offsetString = getTimezoneOffset(timezone);

  return (
    <div css={[dsStyles.flexCenter, styles.timezoneInfo(timezoneTextTopOffset ?? 0)]}>
      <Icon name='network-small' />
      <Text size='small' title={offsetString}>
        {shortTimezone}
      </Text>
    </div>
  );
};

const styles = {
  timezoneInfo: (top: number) =>
    css({
      userSelect: 'none',
      gap: theme.spacing(0.5),
      position: 'sticky',
      gridColumn: '1 / -1',
      gridRow: '1 / -1',
      width: `${HOURS_LANE_WIDTH}px`,
      left: 0,
      zIndex: zIndex.sidebarHoursSection + 1,
      top,
      height: theme.spacing(4),
      background: theme.colors.neutral5,
      color: theme.colors.neutral50,
      marginTop: theme.spacing(-5),
    }),
  container: (noOfHours: number) =>
    css({
      position: 'sticky',
      zIndex: zIndex.sidebarHoursSection,
      display: 'grid',
      gridColumn: '1 / -1',
      backgroundColor: theme.colors.neutral5,
      gridTemplateRows: `repeat(${noOfHours}, ${ADJUSTED_ONE_HOUR_IN_MINUTES}px)`,
      gridRow: '1 / -1',
      width: `${HOURS_LANE_WIDTH}px`,
      left: 0,
    }),
  item: css({
    textAlign: 'end',
    paddingRight: theme.spacing(2),
  }),
};
